import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";
import config from "../../Config";
import FeeSystemMenu from "./FeeSystemMenu";
import RightsCheck from "../General/RightsCheck";
import { scrollToTop } from "../Shared/ScrollOnTop";

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

function yearOptions() {
  const y = new Date().getFullYear();
  const opts = [];
  for (let i = y - 6; i <= y + 1; i++) {
    opts.push({ value: i, label: String(i) });
  }
  return opts;
}

function parseFilenameFromDisposition(header) {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, ""));
  const plain = /filename=([^;]+)/i.exec(header);
  if (plain) return plain[1].trim().replace(/^["']|["']$/g, "");
  return null;
}

function FeePostingReport() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));
  const [SelectedMonth, setSelectedMonth] = useState(11);
  const [FYear, setFYear] = useState(2025);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    if (!SelectedMonth || !FYear) {
      toast.error("Please select Month and Year.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setDownloading(true);
    axios({
      method: "post",
      url: config.base_url + "FeeSystem/ExportFeePostingExcel",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID: parseInt(UserID, 10) || 0,
        MemberType: MemberType || "Admin",
        SelectedMonth: parseInt(SelectedMonth, 10),
        FYear: parseInt(FYear, 10),
      },
      responseType: "blob",
      timeout: 300000,
    })
      .then((res) => {
        const ct = (res.headers["content-type"] || "").toLowerCase();
        if (ct.includes("application/json")) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const j = JSON.parse(reader.result);
              toast.error(j.status_message || "Export failed.", {
                position: toast.POSITION.TOP_RIGHT,
              });
            } catch {
              toast.error("Export failed.", {
                position: toast.POSITION.TOP_RIGHT,
              });
            }
          };
          reader.readAsText(res.data);
          return;
        }

        let fname = `FeePosting_${FYear}_${String(SelectedMonth).padStart(
          2,
          "0"
        )}_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
        const fromHdr = parseFilenameFromDisposition(
          res.headers["content-disposition"]
        );
        if (fromHdr) fname = fromHdr;

        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Downloaded ${fname}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .catch(() => {
        toast.error("Network error or timeout during export.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setDownloading(false));
  };

  return (
    <>
      <RightsCheck />
      <FeeSystemMenu />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/Dashboard">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/FeeSystem/FeeSystemDashboard" onClick={scrollToTop}>
                        Fee System
                      </NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Fee Posting Report
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Download Fee Posting</div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="card">
              <div className="card-body p-md-4">
                <p className="text-muted mb-4">
                  Downloads an Excel file of monthly fee postings from{" "}
                  <strong>tblStudentLedger</strong>: one row per student with
                  Serial No, Student ID, Registration No, Class, Section, and
                  SUM(Debit) for each Fee Code that has debit &gt; 0 in the
                  selected month.
                </p>
                <div className="row align-items-end gy-3">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Month</label>
                      <select
                        className="form-select"
                        value={SelectedMonth}
                        onChange={(e) =>
                          setSelectedMonth(parseInt(e.target.value, 10))
                        }
                      >
                        {MONTH_OPTIONS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Year</label>
                      <select
                        className="form-select"
                        value={FYear}
                        onChange={(e) =>
                          setFYear(parseInt(e.target.value, 10))
                        }
                      >
                        {yearOptions().map((y) => (
                          <option key={y.value} value={y.value}>
                            {y.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    {downloading ? (
                      <Spinner animation="border" role="status" />
                    ) : (
                      <button
                        type="button"
                        className="button button-primary button-icon"
                        onClick={handleDownload}
                      >
                        Download Excel
                        <i className="ri-file-excel-2-line"></i>
                      </button>
                    )}
                  </div>
                </div>
                {downloading && (
                  <p className="text-muted small mt-3 mb-0">
                    Building Excel for all students in the selected month. This
                    may take a minute for large months (5,000–7,000 students).
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FeePostingReport;
